/**
 * FLAV PDP — JavaScript
 * Vanilla JS, no jQuery. Modular IIFE.
 *
 * Handles:
 * 1. Gallery thumbnail switching
 * 2. Pack size selection (update prices)
 * 3. Purchase option toggle (one-time vs subscribe)
 * 4. FAQ accordion
 * 5. Sticky mobile CTA show/hide on scroll
 * 6. Price calculations & display updates
 */

(function () {
  'use strict';

  const pdp = document.querySelector('.flav-pdp');
  if (!pdp) return;
  if (pdp.dataset.initialized === 'true') return;
  pdp.dataset.initialized = 'true';

  /* ---------- Refs ---------- */
  const form = pdp.querySelector('.flav-pdp__form');
  const quantityInput = form?.querySelector('[data-quantity]');
  const subscriptionInput = form?.querySelector('[data-subscription-property]');
  const addToCartBtn = pdp.querySelector('.flav-pdp__add-to-cart');
  const stickyMobile = document.getElementById('flav-sticky-mobile');
  const stickyMobileBtn = stickyMobile?.querySelector('[data-flav-sticky-cta]');
  const stickyCartBadge = stickyMobile?.querySelector('[data-sticky-cart-badge]');
  const stickyCartCount = stickyMobile?.querySelector('[data-sticky-cart-count]');
  const flavorSwatchLinks = pdp.querySelectorAll('[data-flavor-swatch-link]');
  const variantEventRoot = pdp.closest('.shopify-section') || pdp;
  let pageLoaded = document.readyState === 'complete';

  var currentCartCount = 0;

  const basePrice = parseInt(pdp.dataset.basePrice || '0', 10);
  const baseServings = parseInt(pdp.dataset.baseServings || '24', 10);
  const discountPercent = parseInt(pdp.dataset.subscribeDiscount || '18', 10);

  const packOptions = pdp.querySelectorAll('input[name="pack_size"]');
  const purchaseOptions = pdp.querySelectorAll('input[name="purchase_type"]');
  const sellingPlanInput = form?.querySelector('[data-selling-plan-input]');
  const sellingPlanSelect = pdp.querySelector('[data-selling-plan-select]');
  const subscriptionPreferenceStorageKey = 'flav-subscription-preference';
  const subscriptionPreferenceMaxAgeMs = 1000 * 60 * 60 * 4;

  /* ---------- Helpers ---------- */
  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function getSelectedPackQty() {
    const checked = pdp.querySelector('input[name="pack_size"]:checked');
    return checked ? parseInt(checked.value, 10) : 1;
  }

  function isSubscription() {
    const checked = pdp.querySelector('input[name="purchase_type"]:checked');
    return checked?.value === 'subscribe';
  }

  function getSelectedFrequencyDays() {
    if (!(sellingPlanSelect instanceof HTMLSelectElement)) return '';

    var selectedOption = sellingPlanSelect.options[sellingPlanSelect.selectedIndex];
    if (!selectedOption) return '';

    var valueMatch = String(selectedOption.value || '').match(/\d+/);
    if (valueMatch && productHasFrequencyOption(valueMatch[0])) {
      return valueMatch[0];
    }

    var labelMatch = String(selectedOption.textContent || '').match(/(\d+)\s*day/i);
    return labelMatch ? labelMatch[1] : '';
  }

  function getValidSellingPlanIds() {
    if (!(sellingPlanSelect instanceof HTMLSelectElement)) return [];

    return String(sellingPlanSelect.dataset.validSellingPlanIds || '')
      .split(',')
      .map(function (value) {
        return String(value || '').trim();
      })
      .filter(function (value) {
        return /^\d+$/.test(value);
      });
  }

  function getValidatedSellingPlanId(value) {
    var planId = String(value || '').trim();
    if (!/^\d+$/.test(planId)) return '';

    return getValidSellingPlanIds().indexOf(planId) !== -1 ? planId : '';
  }

  function syncSellingPlanInput() {
    if (!sellingPlanInput) return;

    if (!isSubscription() || !(sellingPlanSelect instanceof HTMLSelectElement)) {
      sellingPlanInput.value = '';
      return;
    }

    sellingPlanInput.value = getValidatedSellingPlanId(sellingPlanSelect.value);
  }

  function productHasFrequencyOption(days) {
    if (!(sellingPlanSelect instanceof HTMLSelectElement) || !days) return false;

    return Array.prototype.some.call(sellingPlanSelect.options, function (option) {
      return getFrequencyDaysForOption(option) === String(days);
    });
  }

  function getFrequencyDaysForOption(option) {
    if (!(option instanceof HTMLOptionElement)) return '';

    var valueMatch = String(option.value || '').match(/\d+/);
    if (valueMatch && String(option.textContent || '').match(new RegExp(valueMatch[0] + '\\s*day', 'i'))) {
      return valueMatch[0];
    }

    var labelMatch = String(option.textContent || '').match(/(\d+)\s*day/i);
    return labelMatch ? labelMatch[1] : '';
  }

  function readSubscriptionPreference() {
    try {
      var stored = sessionStorage.getItem(subscriptionPreferenceStorageKey);
      if (!stored) return null;

      var parsed = JSON.parse(stored);
      var savedAt = Number(parsed && parsed.savedAt ? parsed.savedAt : 0);
      var frequencyDays = String(parsed && parsed.frequencyDays ? parsed.frequencyDays : '').trim();

      if (!savedAt || Date.now() - savedAt > subscriptionPreferenceMaxAgeMs) {
        sessionStorage.removeItem(subscriptionPreferenceStorageKey);
        return null;
      }

      if (!frequencyDays) {
        sessionStorage.removeItem(subscriptionPreferenceStorageKey);
        return null;
      }

      return {
        purchaseType: 'subscribe',
        frequencyDays: frequencyDays
      };
    } catch (error) {
      return null;
    }
  }

  function clearSubscriptionPreference() {
    try {
      sessionStorage.removeItem(subscriptionPreferenceStorageKey);
    } catch (error) {
      // Ignore storage failures.
    }
  }

  function persistSubscriptionPreference() {
    if (!isSubscription()) {
      clearSubscriptionPreference();
      return;
    }

    var frequencyDays = getSelectedFrequencyDays();
    if (!frequencyDays) {
      clearSubscriptionPreference();
      return;
    }

    try {
      sessionStorage.setItem(subscriptionPreferenceStorageKey, JSON.stringify({
        purchaseType: 'subscribe',
        frequencyDays: frequencyDays,
        savedAt: Date.now()
      }));
    } catch (error) {
      // Ignore storage failures.
    }
  }

  function applyPersistedSubscriptionPreference() {
    var preference = readSubscriptionPreference();
    if (!preference || preference.purchaseType !== 'subscribe') return;

    var subscribeOption = pdp.querySelector('input[name="purchase_type"][value="subscribe"]');
    if (subscribeOption instanceof HTMLInputElement) {
      subscribeOption.checked = true;
    }

    if (!(sellingPlanSelect instanceof HTMLSelectElement)) return;

    var matchingOption = Array.prototype.find.call(sellingPlanSelect.options, function (option) {
      return getFrequencyDaysForOption(option) === preference.frequencyDays;
    });

    if (matchingOption) {
      sellingPlanSelect.value = matchingOption.value;
    }
  }

  function getSessionCartCount() {
    try {
      var stored = sessionStorage.getItem('cart-count');
      if (!stored) return null;

      var parsed = JSON.parse(stored);
      if (Date.now() - Number(parsed && parsed.timestamp ? parsed.timestamp : 0) >= 10000) {
        return null;
      }

      var count = parseInt(parsed && parsed.value ? parsed.value : '', 10);
      return Number.isNaN(count) ? null : count;
    } catch (error) {
      return null;
    }
  }

  function getKnownCartCount() {
    var headerCount = parseInt(
      (
        document.querySelector('.header-actions__cart-icon .cart-bubble__text-count')?.textContent || ''
      ).trim(),
      10
    );

    if (!Number.isNaN(headerCount)) return headerCount;

    var sessionCount = getSessionCartCount();
    if (sessionCount !== null) return sessionCount;

    var datasetCount = parseInt(stickyMobile?.dataset.cartCount || '0', 10);
    return Number.isNaN(datasetCount) ? 0 : datasetCount;
  }

  function renderStickyCartBadge(count) {
    currentCartCount = Math.max(0, count);

    if (stickyMobile) {
      stickyMobile.dataset.cartCount = String(currentCartCount);
    }

    if (!stickyCartBadge || !stickyCartCount) return;

    stickyCartCount.textContent = currentCartCount > 99 ? '99+' : String(currentCartCount);
    stickyCartBadge.hidden = currentCartCount === 0;
  }

  function isProductFormReady() {
    return pdp.dataset.atcFormReady === 'true';
  }

  function setAddToCartReady(isReady) {
    pdp.dataset.atcReady = isReady ? 'true' : 'false';

    if (addToCartBtn instanceof HTMLButtonElement) {
      if (!addToCartBtn.dataset.flavInitialAvailabilityDisabled) {
        addToCartBtn.dataset.flavInitialAvailabilityDisabled = addToCartBtn.dataset.flavReadyLock === 'true'
          ? 'false'
          : (addToCartBtn.disabled ? 'true' : 'false');
      }

      if (isReady) {
        addToCartBtn.removeAttribute('aria-busy');
        addToCartBtn.disabled =
          addToCartBtn.dataset.flavInitialAvailabilityDisabled === 'true'
          || addToCartBtn.dataset.flavAvailabilityDisabled === 'true';
      } else {
        addToCartBtn.disabled = true;
        addToCartBtn.setAttribute('aria-busy', 'true');
      }
    }

    if (stickyMobileBtn) {
      var stickyDisabled = !isReady
        || (addToCartBtn instanceof HTMLButtonElement && addToCartBtn.disabled);
      stickyMobileBtn.disabled = stickyDisabled;
      stickyMobileBtn.setAttribute('aria-disabled', stickyDisabled ? 'true' : 'false');
    }
  }

  function syncAddToCartReadyState() {
    setAddToCartReady(pageLoaded && isProductFormReady());
  }

  function lockAddToCartUntilReady() {
    syncAddToCartReadyState();

    document.addEventListener('flav:product-form-ready', function (event) {
      if (event.detail?.productId && String(event.detail.productId) !== String(pdp.dataset.productId)) return;

      pdp.dataset.atcFormReady = 'true';
      syncAddToCartReadyState();
    });

    if (!pageLoaded) {
      window.addEventListener('load', function () {
        pageLoaded = true;
        syncAddToCartReadyState();
      }, { once: true });
    }
  }

  function handleStickyCartUpdate(event) {
    var absoluteItemCount = parseInt(String(event.detail?.resource?.item_count ?? ''), 10);
    var eventItemCount = parseInt(String(event.detail?.data?.itemCount ?? ''), 10);
    var source = event.detail?.data?.source;

    if (!Number.isNaN(absoluteItemCount)) {
      renderStickyCartBadge(absoluteItemCount);
      return;
    }

    if (source === 'product-form-component') {
      requestAnimationFrame(function () {
        renderStickyCartBadge(getKnownCartCount());
      });
      return;
    }

    if (!Number.isNaN(eventItemCount)) {
      renderStickyCartBadge(eventItemCount);
      return;
    }

    renderStickyCartBadge(getKnownCartCount());
  }

  function handleStickyCartCountSync(event) {
    var itemCount = parseInt(String(event.detail?.itemCount ?? ''), 10);
    if (Number.isNaN(itemCount)) return;

    renderStickyCartBadge(itemCount);
  }

  /* ---------- 1. Gallery Carousel ---------- */
  function initGallery() {
    var track = pdp.querySelector('#flav-gallery-track');
    if (!track) return;

    var slides = Array.from(track.querySelectorAll('.flav-gallery__slide'));
    var thumbs = Array.from(pdp.querySelectorAll('.flav-gallery__thumb'));
    var dots = Array.from(pdp.querySelectorAll('.flav-gallery__dot'));
    var prevBtn = pdp.querySelector('[data-gallery-prev]');
    var nextBtn = pdp.querySelector('[data-gallery-next]');
    var stage = track.closest('.flav-gallery__stage');
    var currentIndex = 0;
    var total = slides.length;

    if (total <= 1) return;

    function goToIndex(idx) {
      if (idx < 0) idx = total - 1;
      if (idx >= total) idx = 0;
      currentIndex = idx;

      // Compute offset: slide width + gap
      var slideWidth = slides[0].offsetWidth;
      var gapPx = parseFloat(getComputedStyle(track).gap) || 0;
      track.style.transform = 'translateX(' + (-(slideWidth + gapPx) * currentIndex) + 'px)';

      // Sync active states
      slides.forEach(function (s, i) { s.classList.toggle('is-active', i === currentIndex); });
      thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === currentIndex);
        if (i === currentIndex) { t.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }
      });
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === currentIndex); });
    }

    // Thumb clicks
    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { goToIndex(i); });
    });

    // Dot clicks
    dots.forEach(function (d, i) {
      d.addEventListener('click', function () { goToIndex(i); });
    });

    // Arrow buttons
    if (prevBtn) prevBtn.addEventListener('click', function () { goToIndex(currentIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goToIndex(currentIndex + 1); });

    // Touch swipe
    if (stage) {
      var touchStartX = 0;
      var touchStartY = 0;
      stage.addEventListener('touchstart', function (e) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - touchStartX;
        var dy = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy)) {
          goToIndex(currentIndex + (dx < 0 ? 1 : -1));
        }
      }, { passive: true });
    }

    goToIndex(0);
  }

  /* ---------- 2 & 3. Price Updates ---------- */
  function updatePriceDisplay() {
    var qty = getSelectedPackQty();
    var onetimePrice = basePrice * qty;
    var subscribePrice = Math.round(onetimePrice * (100 - discountPercent) / 100);
    var finalPrice = isSubscription() ? subscribePrice : onetimePrice;

    // Update hidden form inputs
    if (quantityInput) quantityInput.value = qty;
    if (subscriptionInput) {
      if (isSubscription()) {
        subscriptionInput.value = 'true';
        subscriptionInput.disabled = false;
      } else {
        subscriptionInput.value = '';
        subscriptionInput.disabled = true;
      }
    }

    // Update pack option prices
    packOptions.forEach(function (option) {
      var optQty = parseInt(option.value, 10) || 1;
      var priceEl = option.closest('.flav-pack-option')?.querySelector('[data-pack-price]');
      if (priceEl) priceEl.textContent = formatMoney(basePrice * optQty);
    });

    // Update all price displays
    pdp.querySelectorAll('[data-product-price]').forEach(function (el) {
      el.textContent = formatMoney(onetimePrice);
    });
    pdp.querySelectorAll('[data-onetime-price]').forEach(function (el) {
      el.textContent = formatMoney(onetimePrice);
    });
    pdp.querySelectorAll('[data-subscribe-price]').forEach(function (el) {
      el.textContent = formatMoney(subscribePrice);
    });
    pdp.querySelectorAll('[data-cart-price], [data-sticky-price], [data-sticky-bar-price]').forEach(function (el) {
      el.textContent = formatMoney(finalPrice);
    });

    // Servings & per-serving
    var servings = Math.max(baseServings * qty, 1);
    var perServingPrice = Math.round(onetimePrice / servings);
    var servingsEl = pdp.querySelector('[data-servings-count]');
    var perServingEl = pdp.querySelector('[data-per-serving-price]');
    if (servingsEl) servingsEl.textContent = servings + ' servings';
    if (perServingEl) perServingEl.textContent = formatMoney(perServingPrice) + ' each';

    // Toggle selected visual state on pack options
    pdp.querySelectorAll('.flav-pack-option').forEach(function (label) {
      var input = label.querySelector('input[name="pack_size"]');
      if (input && input.checked) {
        label.classList.add('is-selected');
      } else {
        label.classList.remove('is-selected');
      }
    });

    // Toggle selected visual state on purchase options
    pdp.querySelectorAll('.flav-purchase-option').forEach(function (label) {
      var input = label.querySelector('input[name="purchase_type"]');
      if (input && input.checked) {
        label.classList.add('is-selected');
      } else {
        label.classList.remove('is-selected');
      }
    });

    // Update per-serving prices
    var onetimePerServing = Math.round(onetimePrice / Math.max(baseServings, 1));
    var subscribePerServing = Math.round(subscribePrice / Math.max(baseServings, 1));
    pdp.querySelectorAll('[data-onetime-per-serving]').forEach(function (el) {
      el.textContent = formatMoney(onetimePerServing) + '/serving';
    });
    pdp.querySelectorAll('[data-subscribe-per-serving]').forEach(function (el) {
      el.textContent = formatMoney(subscribePerServing) + '/serving';
    });

    // Show/hide frequency dropdown (subscribe-details visibility handled by CSS .is-selected)
    var frequencySelect = pdp.querySelector('.flav-purchase-option__frequency');

    // Sync selling_plan hidden input
    syncSellingPlanInput();

    persistSubscriptionPreference();
  }

  function initPackSelector() {
    packOptions.forEach(function (option) {
      option.addEventListener('change', updatePriceDisplay);
    });
  }

  function initPurchaseOptions() {
    purchaseOptions.forEach(function (option) {
      option.addEventListener('change', function () {
        // Qty nudge: when subscribe is selected and qty is 1, bump to 2
        if (option.value === 'subscribe') {
          var qtyDisplay = pdp.querySelector('[data-qty-display]');
          var qtyInput = pdp.querySelector('[data-quantity]');
          var decreaseBtn = pdp.querySelector('[data-qty-decrease]');
          if (qtyDisplay && parseInt(qtyDisplay.textContent, 10) < 2) {
            qtyDisplay.textContent = '2';
            if (qtyInput) qtyInput.value = '2';
            if (decreaseBtn) decreaseBtn.disabled = false;
          }
        }
        updatePriceDisplay();
      });
    });

    // Sync selling plan when frequency dropdown changes
    if (sellingPlanSelect) {
      sellingPlanSelect.addEventListener('change', function () {
        syncSellingPlanInput();
        persistSubscriptionPreference();
      });
    }
  }

  /* ---------- 4. FAQ Accordion ---------- */
  function initFAQ() {
    var faqItems = document.querySelectorAll('.flav-faq__item');
    faqItems.forEach(function (item) {
      var question = item.querySelector('.flav-faq__question');
      var answer = item.querySelector('.flav-faq__answer');
      if (!question || !answer) return;

      question.addEventListener('click', function () {
        var isOpen = item.classList.contains('is-open');

        // Close all others
        faqItems.forEach(function (other) {
          if (other !== item) {
            other.classList.remove('is-open');
            var otherAnswer = other.querySelector('.flav-faq__answer');
            var otherQuestion = other.querySelector('.flav-faq__question');
            if (otherAnswer) otherAnswer.hidden = true;
            if (otherQuestion) otherQuestion.setAttribute('aria-expanded', 'false');
          }
        });

        // Toggle current
        if (isOpen) {
          item.classList.remove('is-open');
          answer.hidden = true;
          question.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('is-open');
          answer.hidden = false;
          question.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ---------- 5. Sticky Mobile CTA ---------- */
  function initStickyMobile() {
    if (!stickyMobile || !addToCartBtn) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          stickyMobile.classList.remove('is-visible');
          stickyMobile.setAttribute('aria-hidden', 'true');
        } else {
          stickyMobile.classList.add('is-visible');
          stickyMobile.setAttribute('aria-hidden', 'false');
        }
      });
    }, { threshold: 0 });

    observer.observe(addToCartBtn);

  }

  /* ---------- 6. Quantity Stepper ---------- */
  function initQtyStepper() {
    var decreaseBtn = pdp.querySelector('[data-qty-decrease]');
    var increaseBtn = pdp.querySelector('[data-qty-increase]');
    var qtyDisplay = pdp.querySelector('[data-qty-display]');
    if (!decreaseBtn || !increaseBtn || !qtyDisplay) return;

    var qty = parseInt(qtyDisplay.textContent, 10) || 1;

    function setQty(newQty) {
      qty = Math.max(1, Math.min(newQty, 99));
      qtyDisplay.textContent = qty;
      if (quantityInput) quantityInput.value = qty;
      decreaseBtn.disabled = qty <= 1;
    }

    decreaseBtn.addEventListener('click', function () { setQty(qty - 1); });
    increaseBtn.addEventListener('click', function () { setQty(qty + 1); });

    setQty(qty); // init state
  }

  /* ---------- 7. AJAX Add to Cart ---------- */
  /* Removed — handled natively by <product-form-component> wrapper in flav-pdp-info.liquid.
     Horizon's ProductFormComponent.handleSubmit() fetches /cart/add, dispatches CartAddEvent,
     and CartIcon picks it up automatically. No custom JS needed. */

  /* ---------- Init ---------- */
  initGallery();
  initPackSelector();
  initPurchaseOptions();
  initQtyStepper();
  initFAQ();
  initStickyMobile();
  lockAddToCartUntilReady();
  variantEventRoot.addEventListener('variant:selected', function (event) {
    if (!(event.target instanceof Node) || !pdp.contains(event.target)) return;
    setAddToCartReady(false);
  });
  variantEventRoot.addEventListener('variant:update', function (event) {
    if (!(event.target instanceof Node) || !pdp.contains(event.target)) return;
    requestAnimationFrame(function () {
      syncAddToCartReadyState();
    });
  });
  applyPersistedSubscriptionPreference();
  flavorSwatchLinks.forEach(function (link) {
    link.addEventListener('click', function () {
      setAddToCartReady(false);
    });
  });
  window.addEventListener('pagehide', function () {
    pageLoaded = false;
    setAddToCartReady(false);
  });
  if (stickyMobile) {
    renderStickyCartBadge(getKnownCartCount());
    document.addEventListener('cart:update', handleStickyCartUpdate);
    document.addEventListener('cart:count-sync', handleStickyCartCountSync);
    window.addEventListener('pageshow', function (event) {
      pageLoaded = true;
      syncAddToCartReadyState();
      if (event.persisted) {
        renderStickyCartBadge(getKnownCartCount());
      }
    });
  }
  updatePriceDisplay();
})();
